/**
 * AtlasAuroraModule — aurora curtains over the Commons on some nights
 * (shader wave stage 4, 2026-09-09). The engine's AuroraMaterial (a 2D noise
 * field extruded down a marched column) on an open cylinder band that rides
 * the player's XZ at AURORA_RADIUS_M and stands AURORA_BASE_M..+HEIGHT up —
 * far enough that walking never parallaxes it, inside the ground camera's
 * far plane; `fog = false` (the night fog would bury it). Which nights have
 * one is `auroraTonight(day, seed)` (lib/stage4_fx, seeded by the world day
 * so every client sees the same sky); brightness follows the night factor.
 * Diag `__aurora()`; lever `__auroraForce(on|null)`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { uNightFactor } from './SkyModule.js';
import { activePreset } from '../lib/quality_tiers.js';
import { createAuroraMaterial } from 'enginex/render/sky/effects/AuroraMaterial.js';
import { auroraTonight, auroraIntensity, dayIndexOf, stage4Stats } from '../lib/stage4_fx.js';
import { skyH } from '../lib/night_sky_schedule.js';

const AURORA_RADIUS_M = 2600;
const AURORA_BASE_M = 1500;
const AURORA_HEIGHT_M = 900;
const AURORA_ARC = 1.35; // radians of the band

export class AtlasAuroraModule implements IModule {
  readonly name = 'Aurora';
  private app!: App;
  private mesh: THREE.Mesh | null = null;
  private uniforms: { intensity: { value: number } } | null = null;
  private force: boolean | null = null;
  private frame = 0;
  private seed = 0;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('AtlasAurora', Phase.RenderSub, 7, () => this.tick()));
    (globalThis as Record<string, unknown>).__aurora = () => ({
      built: !!this.mesh, visible: this.mesh?.visible ?? false, intensity: this.uniforms?.intensity.value ?? 0,
      tonight: this.tonight(), force: this.force, night: (uNightFactor as unknown as { value: number }).value,
    });
    (globalThis as Record<string, unknown>).__auroraForce = (on: boolean | null) => { this.force = on; };
  }

  private tonight(): boolean {
    if (this.force !== null) return this.force;
    const day = dayIndexOf(Date.now() / 86_400_000); // the sky's day count is wall-clock based
    return auroraTonight(day, this.seed);
  }

  private build(): void {
    const { material, uniforms } = createAuroraMaterial({ steps: activePreset().post.ssgi ? 20 : 12, intensity: 0, speed: 0.05, scale: 3 });
    const geo = new THREE.CylinderGeometry(AURORA_RADIUS_M, AURORA_RADIUS_M, AURORA_HEIGHT_M, 48, 1, true, -AURORA_ARC / 2, AURORA_ARC);
    const mesh = new THREE.Mesh(geo, material);
    mesh.name = 'commons-aurora';
    mesh.frustumCulled = false;
    mesh.renderOrder = -5; // behind everything solid, with the sky bodies
    mesh.visible = false;
    this.app.world.scene.add(mesh);
    this.mesh = mesh;
    this.uniforms = uniforms;
    stage4Stats.auroraBuilt++;
  }

  private tick(): void {
    if ((++this.frame & 3) !== 0) return;
    const on = !!activePreset().aurora && !atlas.spaceFlight && atlas.currentWorldId === atlas.commonsWorldId;
    if (!on) { if (this.mesh) this.mesh.visible = false; stage4Stats.auroraVisible = false; return; }
    if (!this.mesh) {
      const w = atlas.conn ? [...atlas.conn.db.world.iter()].find((x) => x.kind === 'commons') : null;
      // The Commons seed is a STRING ('commons-genesis'): Number() made it NaN, the nightly hash
      // collapsed to 0 and the "~35 % of nights" gate opened EVERY night (2026-09-22). Hash it.
      this.seed = Math.floor(skyH(w?.terrainSeed ?? '') * 1_000_000);
      this.build();
    }
    const m = this.mesh!, u = this.uniforms!;
    const night = (uNightFactor as unknown as { value: number }).value ?? 0;
    const v = auroraIntensity(night, this.tonight(), 1);
    u.intensity.value = v;
    m.visible = v > 0.001;
    stage4Stats.auroraVisible = m.visible;
    stage4Stats.auroraIntensity = v;
    if (m.visible) {
      // Ride the player: the band stands to the north-east, its foot AURORA_BASE_M up.
      // ABSOLUTE coordinates: under the FP64 rebase scene.position holds −origin
      // and every child carries absolute coords (adding it back doubled the shift).
      m.position.set(atlas.localX, AURORA_BASE_M + AURORA_HEIGHT_M / 2, atlas.localZ);
      m.rotation.y = 0.6;
    }
  }

  dispose(): void {
    if (this.mesh) { this.app.world.scene.remove(this.mesh); this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh = null; }
  }
}
